/* eslint-disable camelcase */
/* eslint-disable camelcase */

import { LOCATION_CHANGE, replace } from 'connected-react-router';
import { matchPath } from 'react-router';
import { combineEpics, ofType } from 'redux-observable';
import { combineLatest, EMPTY, from, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  pairwise,
  take,
} from 'rxjs/operators';

import { unsubscribeBasketOrderOrderbook } from 'actions';
import { setMarketsSelectedTab } from 'actions/markets';
import {
  optionChainToggleExpiryDate,
  updateOptionChainAssetSymbol,
} from 'actions/optionChain';
import { unsubscribeL2Orderbook, unsubscribeObRt } from 'actions/socket';
import {
  IS_INDIAN_EXCHANGE,
  VANILLA_SETTLING_ASSET,
  STOCKS_METALS_BY_EXCHANGE,
} from 'constants/constants';
import { getLastProduct } from 'helpers/localCache';
import { getNearestStrikePriceForOptionChain } from 'helpers/optionsChain';
import { head, pipe, sortBy } from 'helpers/ramda';
import {
  getDefaultAssetAndExpiryDate,
  getFilteredDataForOptionChains,
  isEmpty,
  isMoveOptions,
  isTradePage,
  isTurbo,
  selectedOptionChainProductList,
} from 'helpers/utils';
import {
  optionsChainSelectedAssetTypeSelector,
  optionsChainSelectedDateSelector,
  optionsInfoSelector,
} from 'selectors/optionChainSeletor';
import {
  assetSelector,
  productsSelector,
  selectedProductSelector,
} from 'selectors/tradeSelectors';
import { TopTag } from 'types/IProducts';
import { resetFormValues } from 'utils/persistClassBasedFormValues';

import { changeSelectedProduct } from '../actions/trade';
import { filterObjectToList, findObject, isNotEmpty } from '../ramdax';

// if (!matchedPath) {
//   matchedPath = matchPath(
//     path,
//     '/tradingview/:chartType/:contractType/:assetSymbol/:productSymbol'
//   );
// }

const chartLocationEpic = (action$, state$) =>
  action$.pipe(
    ofType(LOCATION_CHANGE),
    map(({ payload }) => payload.location.pathname),
    map(path =>
      matchPath(path, '/tradingview/:chartType/:contractType/:assetSymbol/:productSymbol')
    ),
    filter(Boolean),
    filter(match => match.isExact),
    mergeMap(({ params }) => {
      const products = productsSelector(state$.value);
      if (isNotEmpty(products)) {
        return of({ params, products });
      }
      return state$.pipe(
        map(productsSelector),
        filter(isNotEmpty),
        take(1),
        map(_products => ({ params, products: _products }))
      );
    }),
    map(({ params, products }) => dispatch => {
      const { productSymbol } = params;
      if (productSymbol) {
        const product = findObject(({ symbol }) => symbol === productSymbol, products);
        if (isNotEmpty(product)) {
          return dispatch(changeSelectedProduct(product.id));
        }
      }
      return EMPTY;
    })
  );

const findMarketRedirect = ({
  params,
  optionsInfo,
  optionsChainSelectedAsset,
  optionsChainSelectedExpiryDate,
  search,
  // state$,
}) => {
  const { contractType, assetSymbol, expiryDate } = params;

  // Check for 'tab' query parameter to persist TradFi selection on reload
  const searchParams = new URLSearchParams(search);
  const tabParam = searchParams.get('tab');

  // Determine which tab to set based on query parameter
  let tabToSet = contractType;
  if (contractType === 'futures' && tabParam === STOCKS_METALS_BY_EXCHANGE) {
    tabToSet = STOCKS_METALS_BY_EXCHANGE;
  }

  const actions = tabToSet ? [setMarketsSelectedTab(tabToSet)] : [];

  // code
  switch (contractType) {
    case 'options_chain':
    case 'options': {
      // Normalize assetSymbol to uppercase for case-insensitive matching
      const normalizedAssetSymbol = assetSymbol?.toUpperCase();

      // Get available assets from optionsInfo
      const availableAssets =
        optionsInfo
          .filter(({ contract_type }) => contract_type === 'call_options')[0]
          ?.data.map(({ asset }) => asset) || [];

      // Check if normalized asset symbol exists in available assets
      const assetExists =
        !isEmpty(normalizedAssetSymbol) &&
        normalizedAssetSymbol !== 'UNDEFINED' &&
        availableAssets.includes(normalizedAssetSymbol);

      // Determine the asset symbol to use for redirection
      const redirectAssetSymbol = assetExists
        ? normalizedAssetSymbol
        : optionsChainSelectedAsset || 'BTC';

      // Redirect if asset doesn't exist OR if the original case differs from normalized case
      if (!assetExists || (assetExists && assetSymbol !== normalizedAssetSymbol)) {
        actions.push(
          replace({
            pathname: `/${contractType}/markets/${redirectAssetSymbol}`,
            search,
          })
        );
        break;
      }

      const settlementDates = optionsInfo
        .filter(({ contract_type }) => contract_type === 'call_options')[0]
        .data.filter(({ asset }) => asset === normalizedAssetSymbol)[0].settlement_time;

      if (
        isEmpty(expiryDate) ||
        expiryDate === 'undefined' ||
        !settlementDates.find(date => date === expiryDate)
      ) {
        const newExpiryDate = settlementDates.find(
          date => date === optionsChainSelectedExpiryDate
        )
          ? optionsChainSelectedExpiryDate
          : settlementDates[0];

        actions.push(
          replace({
            pathname: `/${contractType}/markets/${normalizedAssetSymbol}/${newExpiryDate}`,
            search,
          })
        );
        break;
      }

      if (expiryDate !== optionsChainSelectedExpiryDate) {
        actions.push(optionChainToggleExpiryDate(expiryDate));
      }

      if (normalizedAssetSymbol !== optionsChainSelectedAsset) {
        actions.push(updateOptionChainAssetSymbol(normalizedAssetSymbol));
      }
      break;
    }
    default:
      break;
  }

  return from(actions);
};

// Noted: tried writing this fn in recursive function call
// but output of all the branches is not same,
// sometimes, we are invoking `changeSelectedProduct` and other times `replace`
const findProductAndRedirect = ({ params, products, assets, search }) => {
  const { contractType, productSymbol, assetSymbol } = params;
  if (productSymbol) {
    const product = findObject(({ symbol }) => symbol === productSymbol, products);
    if (isNotEmpty(product)) {
      // Check if this is a TradeFi product and URL doesn't have type=tradfi(Global Ex.) or type=rwa(Indian Ex.)
      const isTradFiProduct = product.product_specs?.top_tag === TopTag.TradFi;
      const searchParams = new URLSearchParams(search);
      const hasTypeParam = searchParams.get('type') === STOCKS_METALS_BY_EXCHANGE;

      // If it's a TradeFi product but URL doesn't have the query param, redirect with it
      if (isTradFiProduct && !hasTypeParam) {
        return replace({
          pathname: `/${contractType}/trade/${product.underlying_asset.symbol}/${productSymbol}`,
          search: `type=${STOCKS_METALS_BY_EXCHANGE}`,
        });
      }

      return changeSelectedProduct(product.id);
    }
  }

  if (assetSymbol) {
    const product = pipe(
      filterObjectToList(({ contract_type }) => contract_type === contractType),
      filterObjectToList(
        ({ underlying_asset, state }) =>
          underlying_asset.symbol === assetSymbol && state === 'live'
      ),
      sortBy(({ ui_config }) => ui_config.sort_priority),
      head
    )(products);

    if (product && isNotEmpty(product)) {
      // TODO: Move this to epic after selectedProduct
      return replace(`/${contractType}/trade/${assetSymbol}/${product.symbol}`);
    }
  }

  switch (contractType) {
    case 'interest_rate_swaps': {
      const product = pipe(
        filterObjectToList(
          ({ contract_type, state }) =>
            contract_type === 'interest_rate_swaps' && state === 'live'
        ),
        sortBy(({ ui_config }) => ui_config.sort_priority),
        head
      )(products);

      const underlyingAssetSymbol =
        product?.underlying_asset?.symbol || assets[product.underlying_asset_id].symbol;
      return product
        ? replace(`/${contractType}/trade/${underlyingAssetSymbol}/${product?.symbol}`)
        : replace(`/interest_rate_swaps/markets`);
    }
    case 'futures': {
      // Check for type=tradfi(Global Ex.) or type=rwa(Indian Ex.) query parameter
      const searchParams = new URLSearchParams(search);
      const typeParam = searchParams.get('type');
      const isTradFi = typeParam === STOCKS_METALS_BY_EXCHANGE;

      // Check for last visited product (use separate key for TradFi)
      const lastProduct = isTradFi ? getLastProduct('tradfi') : getLastProduct('futures');
      let product;

      // If TradeFi type, load the last visited TradeFi product or first TradeFi product
      if (isTradFi) {
        // 1. First, try to find the last visited TradeFi product
        if (lastProduct) {
          product = findObject(
            p =>
              p.symbol === lastProduct.product &&
              (p.contract_type === 'futures' ||
                p.contract_type === 'perpetual_futures') &&
              p.state === 'live' &&
              p.product_specs?.top_tag === TopTag.TradFi,
            products
          );
        }

        // 2. If no valid stored product was found, try to find a TradeFi product with "gold" tag
        if (isEmpty(product)) {
          const tradFiProducts = filterObjectToList(
            ({ contract_type, state, product_specs }) =>
              (contract_type === 'futures' || contract_type === 'perpetual_futures') &&
              state === 'live' &&
              product_specs?.top_tag === TopTag.TradFi
          )(products);

          // First, try to find PAXGUSDT/USD
          product = tradFiProducts.find(
            p => p.symbol === `PAXG${VANILLA_SETTLING_ASSET}`
          );

          // If PAXGUSDT not found, fall back to sort priority
          if (!product) {
            product = pipe(
              sortBy(({ ui_config }) => ui_config.sort_priority),
              head
            )(tradFiProducts);
          }
        }
      } else {
        // 1. First, try to find the last visited product (excluding TradeFi products).
        if (lastProduct) {
          product = findObject(
            p =>
              p.symbol === lastProduct.product &&
              (p.contract_type === 'futures' ||
                p.contract_type === 'perpetual_futures') &&
              p.state === 'live' &&
              p.product_specs?.top_tag !== TopTag.TradFi,
            products
          );
        }

        // 2. If no valid stored product was found, pick based on the priority from the futures products (excluding TradeFi).
        if (isEmpty(product)) {
          product = pipe(
            filterObjectToList(
              ({ contract_type, state, product_specs }) =>
                (contract_type === 'futures' || contract_type === 'perpetual_futures') &&
                state === 'live' &&
                product_specs?.top_tag !== TopTag.TradFi
            ),
            sortBy(({ ui_config }) => ui_config.sort_priority),
            head
          )(products);
        }
      }

      // Adding this to fix the symbol crash
      let PATHNAME;
      if (product && product?.underlying_asset?.symbol && product?.symbol) {
        PATHNAME = `/futures/trade/${product.underlying_asset.symbol}/${product.symbol}`;
      } else if (lastProduct?.product === `ETH${VANILLA_SETTLING_ASSET}`) {
        PATHNAME = `/futures/trade/ETH/ETH${VANILLA_SETTLING_ASSET}`;
      } else {
        PATHNAME = `/futures/trade/BTC/BTC${VANILLA_SETTLING_ASSET}`;
      }

      // Preserve existing search params and only add/remove the type parameter
      const redirectSearchParams = new URLSearchParams(search);
      if (isTradFi) {
        redirectSearchParams.set('type', STOCKS_METALS_BY_EXCHANGE);
      } else {
        redirectSearchParams.delete('type');
      }

      return replace({
        pathname: PATHNAME,
        search: redirectSearchParams.toString(),
      });
    }

    case 'move_options': {
      const product = pipe(
        filterObjectToList(({ contract_type, state }) => {
          return isMoveOptions(contract_type) && state === 'live';
        }),
        sortBy(({ ui_config }) => ui_config.sort_priority),
        head
      )(products);

      const underlyingAssetSymbol =
        product?.underlying_asset?.symbol || assets[product.underlying_asset_id].symbol;
      return product
        ? replace(`/move_options/trade/${underlyingAssetSymbol}/${product?.symbol}`)
        : replace(`/move_options/markets`);
    }
    case 'turbo_options': {
      const product = pipe(
        filterObjectToList(
          ({ contract_type, state }) => isTurbo(contract_type) && state === 'live'
        ),
        sortBy(({ ui_config }) => ui_config.sort_priority),
        head
      )(products);

      const underlyingAssetSymbol =
        product?.underlying_asset?.symbol || assets[product.underlying_asset_id].symbol;
      return product
        ? replace(`/turbo_options/trade/${underlyingAssetSymbol}/${product?.symbol}`)
        : replace(`/turbo_options/markets`);
    }
    case 'spreads': {
      const product = pipe(
        filterObjectToList(
          ({ contract_type, state }) =>
            (contract_type === 'spreads' || contract_type === 'interest_rate_swaps') &&
            state === 'live'
        ),
        sortBy(({ ui_config }) => ui_config.sort_priority),
        head
      )(products);

      const underlyingAssetSymbol =
        product?.underlying_asset?.symbol || assets[product.underlying_asset_id].symbol;
      return product
        ? replace(`/spreads/trade/${underlyingAssetSymbol}/${product?.symbol}`)
        : replace(`/spreads/markets`);
    }
    case 'spot': {
      const product = pipe(
        filterObjectToList(
          ({ contract_type, state }) => contract_type === 'spot' && state === 'live'
        ),
        sortBy(({ ui_config }) => ui_config.sort_priority),
        head
      )(products);

      const underlyingAssetSymbol =
        product?.underlying_asset?.symbol || assets[product.underlying_asset_id].symbol;
      return product
        ? replace(`/spot/trade/${underlyingAssetSymbol}/${product?.symbol}`)
        : replace(`/spot/markets`);
    }
    case 'options':
    case 'options_chain': {
      const optionsChainList = selectedOptionChainProductList(Object.values(products));

      const { tableData, expiryTimesDropDownList, underlyingAssetSymbolList } =
        optionsChainList;

      const { defaultSelectedAsset, defaultExpiryDate } = getDefaultAssetAndExpiryDate(
        expiryTimesDropDownList,
        underlyingAssetSymbolList
      );

      const selectedType = null;
      const filteredOptions = getFilteredDataForOptionChains(
        tableData,
        defaultExpiryDate,
        defaultSelectedAsset,
        selectedType
      );

      if (filteredOptions.length > 0) {
        const row = getNearestStrikePriceForOptionChain(filteredOptions);
        return replace({
          pathname: `/${contractType}/trade/${defaultSelectedAsset}/${row.symbol}`,
          search,
        });
      }
      const newfilteredOptions = getFilteredDataForOptionChains(
        tableData,
        expiryTimesDropDownList[defaultSelectedAsset] &&
          expiryTimesDropDownList[defaultSelectedAsset][0],
        defaultSelectedAsset,
        selectedType
      );
      if (newfilteredOptions.length > 0) {
        const row = getNearestStrikePriceForOptionChain(newfilteredOptions);
        return replace({
          pathname: `/${contractType}/trade/${defaultSelectedAsset}/${row.symbol}`,
          search,
        });
      }
      return replace(`/options_chain/markets`);
    }
    case 'options_combos': {
      const product = pipe(
        filterObjectToList(
          ({ contract_type, state }) =>
            contract_type === 'options_combos' && state === 'live'
        ),
        sortBy(({ ui_config }) => ui_config.sort_priority),
        head
      )(products);

      const underlyingAssetSymbol =
        product?.underlying_asset?.symbol || assets[product.underlying_asset_id].symbol;
      return product
        ? replace(`/options_combos/trade/${underlyingAssetSymbol}/${product.symbol}`)
        : replace(`/options_combos/markets`);
    }
    default:
      // eslint-disable-next-line consistent-return, no-useless-return
      return;
  }
};

const marketsLocationEpic = (action$, state$) =>
  action$.pipe(
    ofType(LOCATION_CHANGE),
    map(({ payload }) => ({
      pathname: payload.location.pathname,
      search: payload.location.search,
    })),
    map(({ pathname, search }) => {
      const match = matchPath(
        pathname,
        '/:contractType?/markets/:assetSymbol?/:expiryDate?'
      );
      return { match, search };
    }),
    filter(({ match }) => Boolean(match) && match.isExact),
    mergeMap(({ match, search }) => {
      const optionsInfo = optionsInfoSelector(state$.value);
      const optionsChainSelectedAsset = optionsChainSelectedAssetTypeSelector(
        state$.value
      );
      const optionsChainSelectedExpiryDate = optionsChainSelectedDateSelector(
        state$.value
      );

      if (!isEmpty(optionsInfo)) {
        return of({
          params: match?.params,
          optionsInfo,
          optionsChainSelectedAsset,
          optionsChainSelectedExpiryDate,
          search,
          state$,
        });
      }
      return state$.pipe(
        map(optionsInfoSelector),
        filter(isNotEmpty),
        take(1),
        map(_optionsInfo => ({
          params: match?.params,
          optionsInfo: _optionsInfo,
          optionsChainSelectedAsset,
          optionsChainSelectedExpiryDate,
          search,
          state$,
        }))
      );
    }),
    mergeMap(findMarketRedirect)
  );

const tradeLocationEpic = (action$, state$) =>
  action$.pipe(
    ofType(LOCATION_CHANGE),
    distinctUntilChanged((prev, curr) => {
      const prevPath = prev.payload.location.pathname;
      const currPath = curr.payload.location.pathname;
      const prevSearch = prev.payload.location.search;
      const currSearch = curr.payload.location.search;

      const selectedProduct = selectedProductSelector(state$.value);

      // ? Process if path changed OR search params changed OR selectedProduct is null, (distinctUntilChanged needs opposite condition)
      return prevPath === currPath && prevSearch === currSearch && selectedProduct;
    }),
    map(({ payload }) => ({
      path: payload.location.pathname,
      search: payload.location.search,
    })),
    // Filter out spot trade URLs for Indian exchange since they're redirected
    filter(({ path }) => {
      if (IS_INDIAN_EXCHANGE) {
        const spotTradeMatch = matchPath(
          path,
          '/spot/trade/:assetSymbol?/:productSymbol?'
        );
        // Skip processing if this is a spot trade URL on Indian exchange
        return !spotTradeMatch || !spotTradeMatch.isExact;
      }
      return true;
    }),
    map(({ path, search }) => ({
      match: matchPath(path, '/:contractType?/trade/:assetSymbol?/:productSymbol?'),
      search,
    })),
    filter(({ match }) => Boolean(match) && match.isExact),
    mergeMap(({ match, search }) => {
      resetFormValues(); // todo - to be removed once completely moved to placeorder v2
      const initialProducts = productsSelector(state$.value);
      const initialAssets = assetSelector(state$.value);

      if (isNotEmpty(initialProducts) && isNotEmpty(initialAssets)) {
        return of({
          params: match?.params,
          products: initialProducts,
          assets: initialAssets,
          search,
        });
      }

      const products$ = state$.pipe(map(productsSelector), filter(isNotEmpty), take(1));
      const assets$ = state$.pipe(map(assetSelector), filter(isNotEmpty), take(1));

      // we couldn't find the product, Hence waiting for products and assets api to return response.
      return combineLatest([products$, assets$]).pipe(
        map(([products, assets]) => ({ params: match?.params, products, assets, search }))
      );
    }),
    map(findProductAndRedirect),
    filter(action => action !== undefined)
  );

// epic to unsubscribe from orderbook and recent trade when user navigates to other pages than trade page
const tradeUnsubscribeEpic = action$ =>
  action$.pipe(
    ofType(LOCATION_CHANGE),
    map(({ payload }) => ({
      path: payload.location.pathname,
      search: payload.location.search,
    })),
    pairwise(),
    filter(([{ path: previousPath }, { path: currentPath }]) => {
      return isTradePage(previousPath) && !isTradePage(currentPath);
    }),
    mergeMap(() => {
      // unsubscribe from orderbook and recent trade and set selected product to null
      return of(
        unsubscribeL2Orderbook(),
        unsubscribeObRt(),
        unsubscribeBasketOrderOrderbook(),
        changeSelectedProduct(null)
      );
    })
  );

export default combineEpics(
  tradeLocationEpic,
  marketsLocationEpic,
  chartLocationEpic,
  tradeUnsubscribeEpic
);
